import React, { createElement } from 'react';

import {
  create as createNiceModal,
  show as showNiceModal,
  useModal as useNiceModal,
  Provider as NiceModalProvider,
  type NiceModalHandler,
} from '@ebay/nice-modal-react';

/**
 * Props that every modal content component will receive
 */
export type ModalProps = { modal: NiceModalHandler };

/**
 * Re-export the modal handler type for external use
 */
export type ModalHandler = NiceModalHandler;

/**
 * Helper function to create a modal wrapper component
 * This has been moved to a standalone function to make it easier to test
 */
export const createModalWrapper = <P extends { [key: string]: unknown }>(
  Component: React.ComponentType<{ modal: NiceModalHandler } & P>,
): (props: Record<string, unknown>) => React.ReactElement | null => (props: Record<string, unknown>) => {
    const modal = useNiceModal();

    if (modal.visible) {
      return createElement(
        Component,
        { modal, ...props },
      );
    }
    return null;
  };

/**
 * Controller class for managing modals in the application.
 * This class serves as a wrapper around the @ebay/nice-modal-react package.
 */
export class ModalController {
  /**
   * Creates a modal component from a content component.
   * @param Component The content component to wrap in a modal
   * @returns A modal component that can be shown with the show method
   */
  static create<P extends { [key: string]: unknown }>(
    Component: React.ComponentType<{ modal: NiceModalHandler } & P>,
  ) {
    const ModalWrapper = createModalWrapper(Component);
    return createNiceModal(ModalWrapper);
  }

  /**
   * Shows a modal component.
   * @param modal The modal component created with create
   * @param props Props to pass to the modal component
   * @returns A promise that resolves when the modal is closed
   */
  static show<T = unknown>(
    modal: ReturnType<typeof createNiceModal>,
    props?: Record<string, unknown>,
  ): Promise<T> {
    // We need to cast to any here to match the interface expected by NiceModal.show
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return showNiceModal(modal, props as any);
  }

  /**
   * Access the modal hook. This is just a reference to the useModal function.
   * @param modal The modal component created with create
   * @returns The modal handler
   */
  static useModal = useNiceModal;

  /**
   * Provider component for the modal system.
   * This component needs to be at the root of your application.
   * @param props Props with children
   * @returns Provider component
   */
  static Provider: React.FC<React.PropsWithChildren> = ({ children }) => {
    return createElement(
      NiceModalProvider,
      null,
      children,
    );
  };
}
